package com.gloopgo.game.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gloopgo.game.moderation.PlayerReportReason
import gloopgo.shared.generated.resources.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

private enum class SafetyStep {
    Menu,
    Reasons,
    Thanks,
    Blocked
}

@Composable
fun PlayerSafetyDialog(
    playerName: String,
    isBlocked: Boolean,
    onDismiss: () -> Unit,
    onReport: suspend (PlayerReportReason) -> Result<Unit>,
    onBlock: () -> Unit,
    onUnblock: () -> Unit
) {
    var step by remember { mutableStateOf(SafetyStep.Menu) }
    var submitting by remember { mutableStateOf(false) }
    var reportFailed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    AlertDialog(
        onDismissRequest = { if (!submitting) onDismiss() },
        title = {
            Text(
                text = when (step) {
                    SafetyStep.Reasons -> stringResource(Res.string.safety_reporting, playerName)
                    SafetyStep.Thanks -> stringResource(Res.string.safety_thanks)
                    SafetyStep.Blocked -> stringResource(Res.string.safety_block)
                    SafetyStep.Menu -> stringResource(Res.string.safety_title)
                },
                fontWeight = FontWeight.Black,
                fontSize = 18.sp,
                color = MaterialTheme.colorScheme.onSurface
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                when (step) {
                    SafetyStep.Menu -> {
                        Text(
                            text = playerName,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF00E5FF),
                            fontSize = 14.sp
                        )
                        Button(
                            onClick = { step = SafetyStep.Reasons },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFF5252)),
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(
                                text = stringResource(Res.string.safety_report),
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        }
                        OutlinedButton(
                            onClick = {
                                if (isBlocked) {
                                    onUnblock()
                                    onDismiss()
                                } else {
                                    onBlock()
                                    step = SafetyStep.Blocked
                                }
                            },
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(
                                text = stringResource(
                                    if (isBlocked) Res.string.safety_unblock else Res.string.safety_block
                                ),
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                    SafetyStep.Reasons -> {
                        Text(
                            text = stringResource(Res.string.safety_reason_prompt),
                            fontSize = 13.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        ReasonButton(stringResource(Res.string.safety_reason_offensive), submitting) {
                            scope.launch {
                                submitReason(PlayerReportReason.OffensiveName, onReport, onStep = { step = it }, onSubmitting = { submitting = it }, onFailed = { reportFailed = it })
                            }
                        }
                        ReasonButton(stringResource(Res.string.safety_reason_cheating), submitting) {
                            scope.launch {
                                submitReason(PlayerReportReason.Cheating, onReport, onStep = { step = it }, onSubmitting = { submitting = it }, onFailed = { reportFailed = it })
                            }
                        }
                        ReasonButton(stringResource(Res.string.safety_reason_other), submitting) {
                            scope.launch {
                                submitReason(PlayerReportReason.Other, onReport, onStep = { step = it }, onSubmitting = { submitting = it }, onFailed = { reportFailed = it })
                            }
                        }
                        if (submitting) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(22.dp),
                                color = Color(0xFF00E5FF),
                                strokeWidth = 2.dp
                            )
                        }
                        if (reportFailed) {
                            Text(
                                text = stringResource(Res.string.safety_report_failed),
                                color = Color(0xFFFF5252),
                                fontSize = 12.sp
                            )
                        }
                    }
                    SafetyStep.Thanks -> {
                        Text(
                            text = stringResource(Res.string.safety_thanks),
                            fontSize = 14.sp,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    SafetyStep.Blocked -> {
                        Text(
                            text = stringResource(Res.string.safety_blocked_confirm),
                            fontSize = 14.sp,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            }
        },
        confirmButton = {
            if (step == SafetyStep.Thanks || step == SafetyStep.Blocked) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E676)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(
                        text = stringResource(Res.string.safety_close),
                        fontWeight = FontWeight.Bold,
                        color = Color.Black
                    )
                }
            } else {
                Spacer(modifier = Modifier.height(0.dp))
            }
        },
        dismissButton = {
            if (step != SafetyStep.Thanks && step != SafetyStep.Blocked) {
                OutlinedButton(
                    onClick = {
                        if (step == SafetyStep.Reasons) {
                            step = SafetyStep.Menu
                            reportFailed = false
                        } else {
                            onDismiss()
                        }
                    },
                    enabled = !submitting,
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(
                        text = stringResource(if (step == SafetyStep.Reasons) Res.string.privacy_back else Res.string.cancel),
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        },
        containerColor = MaterialTheme.colorScheme.surface,
        shape = RoundedCornerShape(20.dp)
    )
}

@Composable
private fun ReasonButton(label: String, submitting: Boolean, onClick: () -> Unit) {
    OutlinedButton(
        onClick = onClick,
        enabled = !submitting,
        shape = RoundedCornerShape(10.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(text = label, fontWeight = FontWeight.Bold)
    }
}

@Composable
fun AiPlayerDialog(
    playerName: String,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = playerName,
                fontWeight = FontWeight.Black,
                fontSize = 18.sp,
                color = MaterialTheme.colorScheme.onSurface
            )
        },
        text = {
            Text(
                text = stringResource(Res.string.ai_player_body),
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.onSurface
            )
        },
        confirmButton = {
            Button(
                onClick = onDismiss,
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E676)),
                shape = RoundedCornerShape(10.dp)
            ) {
                Text(
                    text = stringResource(Res.string.ai_player_ok),
                    fontWeight = FontWeight.Bold,
                    color = Color.Black
                )
            }
        },
        containerColor = MaterialTheme.colorScheme.surface,
        shape = RoundedCornerShape(20.dp)
    )
}

private suspend fun submitReason(
    reason: PlayerReportReason,
    onReport: suspend (PlayerReportReason) -> Result<Unit>,
    onStep: (SafetyStep) -> Unit,
    onSubmitting: (Boolean) -> Unit,
    onFailed: (Boolean) -> Unit
) {
    onSubmitting(true)
    onFailed(false)
    val result = onReport(reason)
    onSubmitting(false)
    if (result.isSuccess) {
        onStep(SafetyStep.Thanks)
    } else {
        onFailed(true)
    }
}
